import { useRef, useState, type DragEvent, type ChangeEvent } from "react";
import { Box, Button, IconButton, LinearProgress, Typography, alpha } from "@mui/material";
import { CloudUploadOutlined, Close as CloseIcon, InsertDriveFileOutlined } from "@mui/icons-material";
import { useNotify } from "react-admin";
import { useAppTheme } from "@/hooks/useAppTheme";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";
import { uploadDocument, validateFileType } from "@/providers/uploadService";
import { DocumentRecordType, DocumentUploadStatus, DropzoneDocumentType } from "@/types";
import { formatFileSize } from "@/utils/formatters/fileUtils";

// =============================================================================
// Types
// =============================================================================

interface PolicyPdfUploadProps {
  /** Pre-populate vendor assignment */
  vendorIdRef?: string;
  /** Pre-populate client context */
  clientIdRef?: string;
  /** Called with the new document ID after a successful upload */
  /**
   * Called with the new document id after the upload is accepted. May be async
   * (it performs the attach); a rejection means the document was saved but not
   * linked, and the caller reports failure rather than success.
   */
  onUploadComplete: (newDocumentId: string) => void | Promise<void>;
  /** When true, the drop zone expands to fill its parent container */
  fullHeight?: boolean;
}

interface FileEntry {
  file: File;
  status: "pending" | "uploading" | "success" | "error";
  progress: number;
  error?: string;
}

// =============================================================================
// Component
// =============================================================================

export const PolicyPdfUpload = ({
  vendorIdRef,
  clientIdRef,
  onUploadComplete,
  fullHeight = false,
}: PolicyPdfUploadProps) => {
  const { theme, isDarkMode } = useAppTheme();
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [fileEntry, setFileEntry] = useState<FileEntry | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // ─────────────────────────────────────────────────────────────
  // File selection & validation
  // ─────────────────────────────────────────────────────────────
  const handleFileSelected = (file: File) => {
    const validation = validateFileType(file, DropzoneDocumentType.POLICIES);
    if (!validation.valid) {
      notify(validation.error ?? "Invalid file", { type: "error" });
      return;
    }
    setFileEntry({ file, status: "pending", progress: 0 });
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelected(file);
    // Reset so the same file can be re-selected
    e.target.value = "";
  };

  // ─────────────────────────────────────────────────────────────
  // Drag & drop handlers
  // ─────────────────────────────────────────────────────────────
  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelected(file);
  };

  // ─────────────────────────────────────────────────────────────
  // Upload
  // ─────────────────────────────────────────────────────────────
  const handleUpload = async () => {
    if (!fileEntry || fileEntry.status === "uploading") return;

    // Backend requires X-Vendor-Id-Ref header for all uploads
    if (!vendorIdRef) {
      notify("Cannot upload: policy has no carrier assigned", { type: "error" });
      return;
    }

    setFileEntry((prev) => (prev ? { ...prev, status: "uploading", progress: 0 } : null));

    try {
      const result = await uploadDocument(
        {
          id: crypto.randomUUID(),
          file: fileEntry.file,
          name: fileEntry.file.name,
          // Storage-only attachment, not a processing document — stamp the
          // non-processing record type so it isn't mislabelled 'policies'
          // (#6505 / ADR-086). The relation to the policy is written separately
          // via the attachments endpoint (see PolicyPDFView.handleUploadComplete).
          record_type: DocumentRecordType.ATTACHMENT,
          vendor_id_ref: vendorIdRef,
          client_id_ref: clientIdRef,
          storage_only: true,
          mime_type: fileEntry.file.type || "application/pdf",
          status: DocumentUploadStatus.PENDING,
          progress: 0,
          error: null,
        },
        (progress) => {
          setFileEntry((prev) => (prev ? { ...prev, progress } : null));
        }
      );

      // Report success only once BOTH the upload AND the attach land. Awaiting
      // onUploadComplete (which performs the attach) means an attach failure
      // throws into the catch below and shows a single, accurate message —
      // rather than a "success" toast contradicting a later attach warning
      // (this app stacks notifications, so both would persist).
      await onUploadComplete(result.id as string);

      setFileEntry((prev) => (prev ? { ...prev, status: "success", progress: 100 } : null));
      notify("Document uploaded successfully", { type: "success" });

      // Clear after a brief delay so the user sees the success state
      setTimeout(() => setFileEntry(null), 1500);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Upload failed";
      setFileEntry((prev) => (prev ? { ...prev, status: "error", error: message } : null));
      notifyError(err);
    }
  };

  const handleClear = () => {
    setFileEntry(null);
  };

  // ─────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────

  // File is staged — show preview + upload button
  if (fileEntry) {
    const isUploading = fileEntry.status === "uploading";
    const isSuccess = fileEntry.status === "success";

    return (
      <Box sx={{ p: 1.5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            p: 1.5,
            borderRadius: 1,
            border: 1,
            borderColor: isSuccess ? "success.main" : fileEntry.status === "error" ? "error.main" : "divider",
            bgcolor: alpha(
              isSuccess
                ? theme.palette.success.main
                : fileEntry.status === "error"
                  ? theme.palette.error.main
                  : theme.palette.action.hover,
              0.06
            ),
          }}
        >
          <InsertDriveFileOutlined sx={{ color: "text.secondary", fontSize: 20 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" noWrap title={fileEntry.file.name}>
              {fileEntry.file.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatFileSize(fileEntry.file.size)}
            </Typography>
            {isUploading && <LinearProgress variant="indeterminate" sx={{ mt: 0.5, borderRadius: 1 }} />}
            {fileEntry.error && (
              <Typography variant="caption" color="error.main">
                {fileEntry.error}
              </Typography>
            )}
          </Box>
          {!isUploading && (
            <IconButton size="small" onClick={handleClear} title="Remove">
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {fileEntry.status === "pending" || fileEntry.status === "error" ? (
          <Button
            variant="contained"
            size="small"
            onClick={handleUpload}
            startIcon={<CloudUploadOutlined />}
            fullWidth
            sx={{ mt: 1 }}
          >
            Upload
          </Button>
        ) : null}
      </Box>
    );
  }

  // Drop zone — no file staged
  return (
    <Box
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={() => fileInputRef.current?.click()}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 1,
        p: 2,
        m: fullHeight ? 0 : 1.5,
        borderRadius: fullHeight ? 0 : 1,
        border: "2px dashed",
        borderColor: isDragging ? "primary.main" : "divider",
        bgcolor: isDragging
          ? alpha(theme.palette.primary.main, 0.06)
          : alpha(isDarkMode ? theme.palette.grey[800] : theme.palette.grey[50], 0.5),
        cursor: "pointer",
        transition: "border-color 0.15s, background-color 0.15s",
        "&:hover": {
          borderColor: "primary.main",
          bgcolor: alpha(theme.palette.primary.main, 0.04),
        },
        ...(fullHeight && { flex: 1, height: "100%", mb: 2 }),
      }}
    >
      <CloudUploadOutlined sx={{ fontSize: 40, color: "text.secondary" }} />
      <Typography variant="body2" color="text.secondary" textAlign="center">
        Drop a file here or click to browse
      </Typography>
      <Typography variant="caption" color="text.disabled">
        PDF, XLS, XLSX — max 50 MB
      </Typography>

      <input ref={fileInputRef} type="file" accept=".pdf,.xls,.xlsx" onChange={handleInputChange} hidden />
    </Box>
  );
};
